export const personKey = (person) => `${person.person_type}:${person.person_id}:${person.enrollment_id || ""}`;

export const addRelatedPerson = (people, person) =>
  people.some((existing) => personKey(existing) === personKey(person)) ? people : [...people, person];

export const defaultRelationship = (person) => person?.person_type === "staff" ? "CONCERNED_STAFF" : "PRIMARY_SUBJECT";

export const updateRelatedPersonRelationship = (people, key, relationship) =>
  people.map((person) => personKey(person) === key ? { ...person, relationship } : person);

export const removeRelatedPerson = (people, key) => people.filter((person) => personKey(person) !== key);

export const scanIsDuplicate = (previous, value, at, windowMs = 1500) =>
  Boolean(value && previous?.value === value && at - Number(previous?.at || 0) < windowMs);

export const reportingQrBox = (viewfinderWidth, viewfinderHeight) => {
  const edge = Math.max(220, Math.floor(Math.min(viewfinderWidth, viewfinderHeight) * 0.82));
  const size = Math.min(edge, viewfinderWidth, viewfinderHeight);
  return { width: size, height: size };
};

export const REPORT_QR_IMAGE_TYPES = ["image/png", "image/jpeg", "image/webp"];
export const REPORT_QR_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

export const REPORT_TRANSITIONS = {
  ACKNOWLEDGE: ["SUBMITTED", "ESCALATED"],
  START_REVIEW: ["ACKNOWLEDGED", "REOPENED"],
  REQUIRE_ACTION: ["ACKNOWLEDGED", "UNDER_REVIEW"],
  REQUEST_CLARIFICATION: ["SUBMITTED", "ACKNOWLEDGED", "UNDER_REVIEW", "ACTION_REQUIRED"],
  RESOLVE: ["ACKNOWLEDGED", "UNDER_REVIEW", "ACTION_REQUIRED", "RETURNED_FOR_CLARIFICATION", "ESCALATED", "REOPENED"],
  CLOSE: ["RESOLVED"],
  REOPEN: ["RESOLVED", "CLOSED"],
  CANCEL: ["DRAFT", "SUBMITTED", "RETURNED_FOR_CLARIFICATION"],
};

export const visibleReportActions = (report) => {
  const serverAllowed = new Set(report?.allowed_actions || []);
  return Object.keys(REPORT_TRANSITIONS).filter((action) => serverAllowed.has(action) && REPORT_TRANSITIONS[action].includes(report?.status));
};

export const canUseConfidentialRouting = (user) =>
  user?.role === "super_admin" || (user?.effective_capabilities || []).includes("report.confidential_route");

export const canUseConfidentialDirect = (user) =>
  user?.role === "super_admin" || (user?.effective_capabilities || []).includes("report.confidential_direct");

export const confidentialRoutingState = (form) => ({
  confidential_route_mode: form.confidential_route_mode,
  confidential_destination_assignment_id: form.confidential_destination_assignment_id,
  confidential_route_reason: form.confidential_route_reason,
});

export const restoreConfidentialRouting = (form, routing) => ({ ...form, ...routing });

export const timelineAuthorityText = (metadata = {}) => {
  const from = metadata.from_assignment_display || metadata.from_assignee;
  const to = metadata.to_assignment_display || metadata.to_assignee;
  if (from && to) return `${from} → ${to}`;
  const assigned = metadata.assigned_to_display || metadata.destination_assignment_display;
  return assigned ? `Assigned to ${assigned}` : "";
};

export const REPORTING_CAPABILITY_CONTROLS = [
  ["report.create", "Create reports"], ["report.view_own", "View authored reports"],
  ["report.view_assigned", "Assigned report visibility"], ["report.view_unit", "Unit report visibility"],
  ["report.view_institution", "Institution-wide report visibility"], ["report.action", "Perform report lifecycle actions"],
  ["report.escalate", "Escalate reports"], ["report.reassign", "Reassign reports"],
  ["report.resolve", "Resolve reports"], ["report.close", "Close reports"], ["report.reopen", "Reopen reports"],
  ["report.confidential_route", "Confidential upward routing"],
  ["report.confidential_direct", "Confidential Direct Message / Assignment"],
  ["report.manage_sensitive", "View and manage sensitive reports"],
  ["report.parent_engagement.create", "Create parent engagement"], ["report.parent_engagement.send", "Send parent engagement"],
  ["report.parent_engagement.view", "View parent engagement"], ["report.analytics.view", "View reporting analytics"],
];

export const validateReportQrImage = (file) => {
  if (!file || !REPORT_QR_IMAGE_TYPES.includes(String(file.type || "").toLowerCase())) {
    return "Choose a PNG, JPEG or WebP image.";
  }
  if (Number(file.size || 0) > REPORT_QR_IMAGE_MAX_BYTES) {
    return "QR image must be 5 MB or smaller.";
  }
  return "";
};

export const REPORT_PRIMARY_ACTION_CLASS = "inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-[#1B2C5A] bg-[#1B2C5A] px-5 py-2.5 font-semibold text-white shadow-md hover:bg-[#14224A] hover:text-white disabled:cursor-not-allowed disabled:opacity-50";

export const REPORT_PRIMARY_ACTION_STYLE = {
  backgroundColor: "#1B2C5A",
  borderColor: "#1B2C5A",
  color: "#FFFFFF",
  opacity: 1,
  boxShadow: "0 6px 14px rgba(27, 44, 90, 0.24)",
};

export const stopScannerAndTracks = async (instance, container) => {
  const videos = [...(container?.querySelectorAll?.("video") || [])];
  const streams = videos.map((video) => video.srcObject).filter(Boolean);
  let stopPromise = Promise.resolve();
  try { if (instance?.isScanning) stopPromise = Promise.resolve(instance.stop()); } catch {}
  streams.forEach((stream) => stream.getTracks?.().forEach((track) => track.stop()));
  try { await stopPromise; } catch {}
  try { await instance?.clear?.(); } catch {}
  const remainingVideos = [...(container?.querySelectorAll?.("video") || [])];
  [...videos, ...remainingVideos].forEach((video) => {
    video.srcObject?.getTracks?.().forEach((track) => track.stop());
    try { video.srcObject = null; } catch {}
  });
};

export const parentSafeEngagement = ({ guardian_id, student_id, subject, message, request_key }) => ({
  guardian_id, student_id, subject, message, request_key,
});

export const relationshipLabel = (value = "") => value.replaceAll("_", " ").toLowerCase().replace(/^./, (c) => c.toUpperCase());

export const groupGuardiansByStudent = (groups = []) => groups.map((group) => ({
  student_id: group.student_id,
  student_name: group.student_name,
  guardians: group.guardians || [],
}));

export const attachmentIsAllowed = (file) =>
  ["image/jpeg", "image/png", "application/pdf"].includes(file?.type) && Number(file?.size || 0) <= 3 * 1024 * 1024;
